import { useState } from 'react'

import type { FormikErrors } from 'formik'
import { styled, css } from 'styled-components'

import { PayMethodIcon } from '@b-stock/fe-core/bstock-next/components'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { PaymentMethodTag } from '../PaymentMethodTag'
import type { PaymentRestrictionReason } from '../utils'

const CardDetail = styled.div<{ $disabled?: boolean }>`
  display: flex;
  ${Typography.Subtitle2}
  color: ${({ $disabled }) =>
    $disabled
      ? Colors.Semantic.BStock.disabled
      : Colors.Semantic.Neutral.black};
  font-weight: bold;
  margin-top: 1rem;
  margin-bottom: 1rem;
  align-items: center;
  flex: 1 0 0;
  justify-content: space-between;
  // Let the badges wrap under the label on narrow screens instead of overflowing.
  flex-wrap: wrap;
  row-gap: 0.5rem;
`

const RestrictedTag = styled.div`
  ${Typography.Body2}
  display: inline-block;
  background-color: ${Colors.Semantic.SpecificFunctions.Tags.warning};
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  border-radius: 0.25rem;
  padding: 0.1875rem 0.5rem;
  font-weight: bold;
  margin-left: 0.5rem;
  margin-right: auto;
`

const UnavailableTag = styled(RestrictedTag)`
  background-color: ${Colors.Semantic.Neutral.medium_grey};
`

// Groups the Default and Selected tags so a method can carry both, left-aligned
// after the method label. Wraps the badges on very narrow widths.
const TagGroup = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: 0.5rem;
  margin-right: auto;
`

const Card = styled.div<{
  $focused?: boolean
  $filled?: boolean
  $disabled?: boolean
  $unavailable?: boolean
  $error?: boolean
  $selected?: boolean
}>`
  background: ${Colors.Semantic.Neutral.white};
  cursor: pointer;
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  display: flex;
  // min-height (not fixed height) so the card grows instead of clipping when the
  // label + badges wrap on narrow screens; single-line cards look unchanged.
  min-height: 4.5rem;
  border-radius: 0.25rem;
  padding: 0.8125rem 1rem;
  align-items: center;
  gap: 0.75rem;
  flex: 1 0 0;
  border-radius: 0.25rem;
  margin-bottom: 1rem;

  ${({ $focused }) =>
    $focused &&
    css`
      border: 0.0625rem solid ${Colors.Semantic.BStock.default.lightBackground};
    `}

  ${({ $error }) =>
    $error &&
    css`
      border: 0.0625rem solid ${Colors.Semantic.Error.onhover};
      background: ${Colors.Semantic.Error.background};
    `}

  ${({ $disabled, $unavailable }) =>
    ($disabled || $unavailable) &&
    css`
      background: ${
        $unavailable
          ? Colors.Semantic.Neutral.light_grey
          : Colors.Semantic.Warning.background
      };
      pointer-events: none;
      border: none;
    `}

  ${({ $selected }) =>
    $selected &&
    css`
      border: 0.125rem solid ${Colors.Semantic.BStock.onhover};
      padding: 1rem;
    `}
`

const CardContent = styled.div`
  flex: 1;
`

export type CardDetails = {
  last4: string
  expMonth: number
  expYear: number
  brand: string
}

export interface UsBankDetails {
  bank_name: string
  account_type: string
  last4: string
}

export type Details = Partial<CardDetails> & {
  last4: string
  us_bank_account?: UsBankDetails
}

/** support for bank, card, and stripe-fin-conn */
const PaymentMethodCard = ({
  name,
  type,
  owner,
  details,
  value,
  onClick,
  error,
  totalAmountRemaining,
  selected,
  disableInput,
  isDefault,
  defaultTagBackgroundColor,
  isSelected,
  skipAmountValidation = false,
  restrictionReason,
}: {
  name: string
  type: string
  owner: string
  details: Details
  value: number // amount to pay
  onClick?: (name: string) => void
  error?: FormikErrors<string>
  totalAmountRemaining: number
  selected?: boolean
  disableInput?: boolean
  isDefault?: boolean
  defaultTagBackgroundColor?: string
  /** Marks the method as the buyer's autopay selection for this listing/contract. */
  isSelected?: boolean
  /**
   * When true, skips validation that would disable the card
   * based on value and totalAmountRemaining.
   * Use this when selecting payment methods without executing a payment.
   * @default false
   */
  skipAmountValidation?: boolean
  restrictionReason?: PaymentRestrictionReason
}) => {
  const { brand } = details
  const bankName = details.us_bank_account?.bank_name
  const last4 = details.last4 || details.us_bank_account?.last4

  const [isFocused] = useState(false)
  const hasInteracted = isFocused || (!isNaN(value) && value !== 0)

  const isAchUnavailable = restrictionReason === 'ach_unavailable'
  const isUnavailable =
    restrictionReason === 'listing_cc_disabled' || isAchUnavailable
  const isAccountRestricted = restrictionReason === 'account_restricted'

  const cardStateProps = {
    $focused: false,
    $filled: false,
    $disabled: false,
    $unavailable: isUnavailable,
    $error: false,
  }
  if (isFocused) {
    cardStateProps.$focused = true
  }
  if (value) {
    cardStateProps.$filled = true
  }
  if (error) {
    cardStateProps.$error = true
  }
  if (
    disableInput ||
    isAccountRestricted ||
    (!skipAmountValidation && !hasInteracted && totalAmountRemaining <= 0)
  ) {
    cardStateProps.$disabled = true
  }

  const isTextMuted = cardStateProps.$disabled || isUnavailable

  return (
    <Card
      data-testid="PaymentMethodCard"
      {...cardStateProps}
      $selected={selected}
      onClick={() => onClick && onClick(name)}
    >
      <PayMethodIcon payMethodName={brand || type} disabled={isTextMuted} />
      <CardContent>
        <CardDetail $disabled={isTextMuted}>
          <FormattedMessage
            id={`PaymentMethodCard.${type}.detail`}
            values={{
              last4: last4 ?? '',
              name: owner,
              bankName: bankName ?? '',
            }}
          />
          {(isDefault || isSelected) &&
            !cardStateProps.$disabled &&
            !isUnavailable && (
              <TagGroup>
                {isDefault && (
                  <PaymentMethodTag
                    variant="default"
                    backgroundColor={defaultTagBackgroundColor}
                  />
                )}
                {isSelected && <PaymentMethodTag variant="selected" />}
              </TagGroup>
            )}
          {isAchUnavailable && (
            <UnavailableTag data-testid="PaymentMethodUnavailableTag">
              <FormattedMessage id="BlockedAchPicker.unavailable" />
            </UnavailableTag>
          )}
          {isAccountRestricted && (
            <RestrictedTag data-testid="PaymentMethodRestrictedTag">
              <FormattedMessage id="PaymentMethodCard.restricted" />
            </RestrictedTag>
          )}
        </CardDetail>
      </CardContent>
    </Card>
  )
}

export default PaymentMethodCard
